import { Button, Empty, Flex, Table, Tabs, Tag, Typography } from "antd"
import type { ColumnsType } from "antd/es/table"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import { generateDocumentTitle } from "../../constants/document-title.ts"
import { RoutesPaths } from "../../constants/routes.ts"
import { TABLE_SURFACES } from "../../components/EntityTable/surfaces.ts"
import { ResizableHeaderCell } from "../../components/Table/ResizableHeaderCell.tsx"
import { useAppSelector } from "../../hooks/use-app-selector.ts"
import { useColumnResize } from "../../hooks/use-column-resize.tsx"
import { useTableColumnConfig } from "../../hooks/use-table-column-config.tsx"
import { getFormatDate } from "../../utils/date/get-date-safe.ts"
import { getEntityName } from "../../utils/user/get-entity-name.ts"
import { selectActiveBranchId } from "../Branches/redux/branches.slice.ts"
import { REVIEW_STATUS_COLOR } from "../Files/helpers/review-status-color.ts"
import {
  getMyPendingReviews,
  getReviewsSentByMe
} from "../Project/api/project-attachment-workflow.services.ts"
import { type DocumentReview } from "../Project/types/project-attachment-workflow.types.ts"

const { Title, Text } = Typography

type Tab = "myApprovals" | "sentForReview"

function useReviewList(tab: Tab, activeTab: Tab) {
  const [reviews, setReviews] = useState<DocumentReview[] | null>(null)
  const [loadFailed, setLoadFailed] = useState(false)
  const [reloadCount, setReloadCount] = useState(0)
  // REOPENED branch-isolation audit: this cross-project Approval view
  // (My Approvals / Sent for Review) never sent the selected branch, so it
  // showed every branch's pending reviews under every workspace. The
  // server already supports `?team=` here (`apps.user.branch_scope`,
  // re-validated against the caller's real accessible teams) — only the
  // frontend call was missing it.
  const activeBranchId = useAppSelector(selectActiveBranchId)

  useEffect(() => {
    if (tab !== activeTab) return
    let cancelled = false
    setReviews(null)
    setLoadFailed(false)
    const fetcher =
      tab === "myApprovals" ? getMyPendingReviews : getReviewsSentByMe
    void fetcher(activeBranchId ?? undefined).then((response) => {
      if (cancelled) return
      if (!response.ok || !response.data) {
        setReviews(null)
        setLoadFailed(true)
        return
      }
      setReviews(response.data.results)
    })
    return () => {
      cancelled = true
    }
  }, [tab, activeTab, reloadCount, activeBranchId])

  return {
    reviews,
    loadFailed,
    retry: () => setReloadCount((count) => count + 1)
  }
}

function buildColumns(
  tab: Tab,
  t: (key: string) => string
): ColumnsType<DocumentReview> {
  return [
    {
      title: t("documentReviews.columns.fileName"),
      dataIndex: "file_name",
      key: "file_name",
      render: (fileName: string) => <Text strong>{fileName}</Text>
    },
    {
      title: t("documentReviews.columns.project"),
      dataIndex: "project_name",
      key: "project_name",
      render: (projectName: string, record) => (
        <Link to={RoutesPaths.project.view(record.project_id).files()}>
          {projectName}
        </Link>
      )
    },
    {
      title:
        tab === "myApprovals"
          ? t("documentReviews.columns.requestedBy")
          : t("documentReviews.columns.reviewer"),
      key: "person",
      render: (_, record) =>
        getEntityName(
          tab === "myApprovals" ? record.requested_by : record.reviewer
        )
    },
    {
      title: t("documentReviews.columns.status"),
      dataIndex: "status",
      key: "status",
      render: (_, record) => (
        <Tag color={REVIEW_STATUS_COLOR[record.status]}>
          {record.status_label}
        </Tag>
      )
    },
    {
      title: t("documentReviews.columns.dueDate"),
      dataIndex: "due_date",
      key: "due_date",
      render: (dueDate: string | null) =>
        dueDate ? getFormatDate(dueDate) : "—"
    },
    {
      title: t("documentReviews.columns.requested"),
      dataIndex: "created",
      key: "created",
      render: (created: string) => getFormatDate(created)
    },
    {
      title: t("documentReviews.columns.actions"),
      key: "actions",
      render: (_, record) => (
        <Flex gap="small">
          <Link to={RoutesPaths.document_reviews.view(record.id)}>
            {t("documentReviews.reviewAction")}
          </Link>
          <Text type="secondary">·</Text>
          <Link
            to={RoutesPaths.project.view(record.project_id).files({
              fileId: record.attachment
            })}
          >
            {t("documentReviews.viewInFiles")}
          </Link>
        </Flex>
      )
    }
  ]
}

export function DocumentReviewsPage() {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState<Tab>("myApprovals")

  useEffect(() => {
    generateDocumentTitle(t("documentReviews.title"))
  }, [t])

  const myApprovals = useReviewList("myApprovals", activeTab)
  const sentForReview = useReviewList("sentForReview", activeTab)

  const myApprovalsColumnConfig = useTableColumnConfig(
    TABLE_SURFACES.documentReviews,
    "myApprovals",
    buildColumns("myApprovals", t)
  )
  const myApprovalsResize = useColumnResize<DocumentReview>({
    fullConfig: myApprovalsColumnConfig.fullConfig,
    setColumns: myApprovalsColumnConfig.setColumns
  })

  const sentForReviewColumnConfig = useTableColumnConfig(
    TABLE_SURFACES.documentReviews,
    "sentForReview",
    buildColumns("sentForReview", t)
  )
  const sentForReviewResize = useColumnResize<DocumentReview>({
    fullConfig: sentForReviewColumnConfig.fullConfig,
    setColumns: sentForReviewColumnConfig.setColumns
  })

  const renderTable = (
    { reviews, loadFailed, retry }: ReturnType<typeof useReviewList>,
    tab: Tab
  ) => {
    if (loadFailed) {
      return (
        <Empty description={t("documentReviews.loadFailed")}>
          <Button onClick={retry}>{t("documentReviews.retry")}</Button>
        </Empty>
      )
    }

    if (reviews !== null && reviews.length === 0) {
      return (
        <Empty
          description={
            tab === "myApprovals"
              ? t("documentReviews.myApprovalsEmpty")
              : t("documentReviews.sentForReviewEmpty")
          }
        />
      )
    }

    const { columns, decorateColumns, tableWrapRef } =
      tab === "myApprovals"
        ? { ...myApprovalsColumnConfig, ...myApprovalsResize }
        : { ...sentForReviewColumnConfig, ...sentForReviewResize }

    return (
      <div ref={tableWrapRef}>
        <Table
          rowKey="id"
          columns={decorateColumns(columns)}
          components={{ header: { cell: ResizableHeaderCell } }}
          dataSource={reviews ?? []}
          loading={reviews === null}
          pagination={false}
        />
      </div>
    )
  }

  return (
    <div className="grid size-full">
      <Flex vertical gap="middle" className="overflow-auto">
        <Flex vertical>
          <Title className="text-start" level={2}>
            {t("documentReviews.title")}
          </Title>
          <Text type="secondary">{t("documentReviews.subtitle")}</Text>
        </Flex>

        <Tabs
          activeKey={activeTab}
          onChange={(key) => setActiveTab(key as Tab)}
          items={[
            {
              key: "myApprovals",
              label: t("documentReviews.myApprovals"),
              children: renderTable(myApprovals, "myApprovals")
            },
            {
              key: "sentForReview",
              label: t("documentReviews.sentForReview"),
              children: renderTable(sentForReview, "sentForReview")
            }
          ]}
        />
      </Flex>
    </div>
  )
}
